import { useState, useEffect, useCallback, useMemo } from 'react';
import { Link } from 'react-router-dom';
import {
  WifiIcon,
  MenuIcon as SidebarToggleIcon,
  SearchIcon,
} from '@/lib/polaris-icons';
import { InlineStack, TopBar, Icon, Text, useBreakpoints } from '@/lib/polaris';
import { useOnlineStatus } from '@smarty/shared';
import { TenantSwitcher } from '@/components/admin/TenantSwitcher';
import { SmartyLogo } from '@/components/brand/SmartyLogo';
import { BilleteraMenu } from '@/components/dropshipping/BilleteraMenu';
import { WaChatsMenu } from '@/components/whatsapp/WaChatsMenu';
import { NotificacionesMenu } from '@/components/layout/NotificacionesMenu';

interface TopbarProps {
  onOpenSearch?: () => void;
  onNavigationToggle?: () => void;
  sidebarCollapsed?: boolean;
  onToggleSidebar?: () => void;
}

export function Topbar({ onOpenSearch, onNavigationToggle, sidebarCollapsed, onToggleSidebar }: TopbarProps) {
  const isOnline = useOnlineStatus();
  // Mobile: ocultamos la search field inline y mostramos solo un icono lupa.
  const { mdDown: isMobile } = useBreakpoints();
  const [isConnectionMenuOpen, setIsConnectionMenuOpen] = useState(false);

  // Atajo ⌘K / Ctrl+K abre el buscador global unificado (entidades + navegación + acciones).
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.key === 'k' || e.key === 'K') && (e.metaKey || e.ctrlKey)) {
        e.preventDefault();
        onOpenSearch?.();
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [onOpenSearch]);

  const toggleIsConnectionMenuOpen = useCallback(() => setIsConnectionMenuOpen((prev) => !prev), []);

  // Acciones del menú de conexión
  const connectionMenuActions = useMemo(() => [{
    items: [{
      content: isOnline ? 'Conectado' : 'Sin conexión',
      icon: WifiIcon,
      badge: isOnline ? { tone: 'new' as const, content: 'Activo' } : undefined,
      disabled: true,
    }],
  }], [isOnline]);

  // Lockup de marca en el contextControl: [hamburguesa] [isotipo] [wordmark],
  // un ÚNICO grupo flex left-aligned. Antes la hamburguesa era un botón
  // position:fixed superpuesto + padding-left:56px de compensación + ancho
  // sincronizado al sidebar (tres mecanismos peleando → spacing impredecible).
  // Ahora todo vive acá y el espaciado es determinista (gap del flex).
  // - Hamburguesa: solo desktop (en mobile la maneja el navigationToggle nativo).
  // - Wordmark: se oculta con el sidebar colapsado (no cabe en el rail de 56px).
  const logoMarkup = useMemo(() => (
    <div style={{ display: 'inline-flex', alignItems: 'center', gap: 6, whiteSpace: 'nowrap', paddingLeft: 4 }}>
      {!isMobile && onToggleSidebar && (
        <button
          type="button"
          className="sf-topbar-hamburger"
          onClick={onToggleSidebar}
          aria-label={sidebarCollapsed ? 'Expandir menú' : 'Colapsar menú'}
          title={sidebarCollapsed ? 'Expandir menú' : 'Colapsar menú'}
        >
          <Icon source={SidebarToggleIcon} />
        </button>
      )}
      <Link
        to="/home"
        aria-label="Inicio"
        style={{ display: 'flex', alignItems: 'center', gap: 8, textDecoration: 'none' }}
      >
        <SmartyLogo size={24} color="#fff" title="Smarty" />
        <span style={{ color: '#fff', fontSize: 15, fontWeight: 600, letterSpacing: '-0.01em' }}>
          Smarty
        </span>
      </Link>
    </div>
  ), [isMobile, sidebarCollapsed, onToggleSidebar]);

  const connectionMenuMarkup = (
    <TopBar.Menu
      activatorContent={
        <span>
          <Icon source={WifiIcon} tone={isOnline ? "success" : "critical"} />
          <Text as="span" visuallyHidden>
            Estado de conexión
          </Text>
        </span>
      }
      open={isConnectionMenuOpen}
      onOpen={toggleIsConnectionMenuOpen}
      onClose={toggleIsConnectionMenuOpen}
      actions={connectionMenuActions}
    />
  );

  // En mobile agregamos un botón lupa antes de los iconos secundarios que
  // abre el buscador (mismo handler que ⌘K). Reemplaza la search field inline.
  const mobileSearchButton = isMobile ? (
    <button
      type="button"
      onClick={() => onOpenSearch?.()}
      aria-label="Abrir búsqueda"
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: 36,
        height: 36,
        background: 'transparent',
        border: 'none',
        borderRadius: 8,
        cursor: 'pointer',
        color: 'inherit',
      }}
    >
      <Icon source={SearchIcon} />
    </button>
  ) : null;

  const secondaryMenuMarkup = (
    <InlineStack gap="200" blockAlign="center" wrap={false}>
      {mobileSearchButton}
      {/*
        ⭐ EL SALDO VA PRIMERO, A LA IZQUIERDA DE TODO. Lo pidió el founder mirándolo: estaba metido
        entre los íconos —después de wifi y chats, antes de la campana— y ahí compite con avisos que
        no deciden nada. El saldo SÍ decide: es lo que le dice al revendedor si puede seguir
        pidiendo. Es el único de los cuatro que lleva un número, y un número entre íconos se lee
        antes si está en una punta que si está en el medio.

        🪤 Y SE ESCONDE SOLO: si el negocio nunca tocó Smarty Drop, el componente no dibuja nada.
        Un ícono permanente que no aplica es ruido, y encabezando la fila sería el ruido más caro.
      */}
      <BilleteraMenu />
      {connectionMenuMarkup}
      {/* Chats antes que notificaciones: un cliente esperando respuesta es más urgente que un
          aviso del sistema, y el orden del encabezado se lee de izquierda a derecha. */}
      <WaChatsMenu />
      <NotificacionesMenu />
      {/* Cambio de tienda + cuenta + cerrar sesión (reemplaza al user menu del avatar). */}
      <TenantSwitcher />
    </InlineStack>
  );

  // Trigger del buscador estilo Shopify: "Buscar" a la izquierda + badge "Ctrl K"
  // a la derecha. El badge es pointer-events:none, así el click siempre enfoca el
  // campo y abre el modal.
  const searchFieldMarkup = (
    <div className="sf-topbar-search">
      <TopBar.SearchField
        value=""
        placeholder="Buscar"
        showFocusBorder
        onChange={() => {}}
        onFocus={() => onOpenSearch?.()}
      />
      <span className="sf-topbar-search__kbd" aria-hidden="true">
        <kbd>Ctrl</kbd>
        <kbd>K</kbd>
      </span>
    </div>
  );

  return (
    <TopBar
      showNavigationToggle
      onNavigationToggle={onNavigationToggle}
      contextControl={logoMarkup}
      secondaryMenu={secondaryMenuMarkup}
      searchField={isMobile ? undefined : searchFieldMarkup}
    />
  );
}
